import { useState, useMemo, useCallback } from "react";
import { Upload, Image, X, Send, FileText, Type } from "lucide-react";
import { notification } from "antd";
import api from "../../API/api";
import useFormDraft from "../../hooks/useFormDraft";

const MAX_IMAGE_DRAFT_CHARS = 2 * 1024 * 1024;

export default function NewPublicationPage() {
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const draftData = useMemo(
    () => ({
      title,
      text,
      imagePreview:
        imagePreview && imagePreview.length <= MAX_IMAGE_DRAFT_CHARS
          ? imagePreview
          : null,
    }),
    [title, text, imagePreview],
  );

  const setDraftData = useCallback((data) => {
    setTitle(data.title ?? "");
    setText(data.text ?? "");
    setImagePreview(data.imagePreview ?? null);
    setImage(null);
  }, []);

  const {
    markEdited,
    clearDraft,
    draftModals,
    draftBanner,
  } = useFormDraft({
    namespace: "publicacao-nova",
    data: draftData,
    setData: setDraftData,
    enabled: true,
    ready: true,
    notice:
      imagePreview && imagePreview.length > MAX_IMAGE_DRAFT_CHARS
        ? "A imagem é demasiado grande para o rascunho local; apenas o texto foi guardado."
        : undefined,
  });

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      markEdited();
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    markEdited();
    setImage(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const form = new FormData();
      form.append("title", title);
      form.append("description", text);
      if (image) {
        form.append("image", image);
      }

      const result = await api.post("v1/publications/", form);
      if (result.status === 201) {
        setTitle("");
        setText("");
        setImage(null);
        setImagePreview(null);
        await clearDraft();
        notification.success({
          message: "Sucesso",
          description: "Publicação criada com sucesso!",
          placement: "topRight",
        });
      } else {
        notification.error({
          message: "Erro",
          description: "Falha ao criar publicação.",
          placement: "topRight",
        });
      }
    } catch (error) {
      console.error("Erro ao criar publicação:", error);
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao criar a publicação.",
        placement: "topRight",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full py-6">
      {draftModals}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden w-full">
        {draftBanner}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <Type className="w-4 h-4 text-emerald-500" />
                Título
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => {
                  markEdited();
                  setTitle(e.target.value);
                }}
                placeholder="Digite o título da publicação..."
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                required
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <FileText className="w-4 h-4 text-emerald-500" />
                Descrição
              </label>
              <textarea
                value={text}
                onChange={(e) => {
                  markEdited();
                  setText(e.target.value);
                }}
                placeholder="Escreva uma descrição detalhada..."
                rows={6}
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 transition-all resize-none"
                required
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <Image className="w-4 h-4 text-emerald-500" />
                Imagem
              </label>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <label className="flex-1 flex items-center justify-center gap-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg px-4 py-2.5 cursor-pointer transition-colors">
                    <Upload className="w-4 h-4 text-emerald-500" />
                    <span className="text-sm text-gray-600">
                      Escolher Imagem
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                  <span className="text-xs text-gray-400">
                    JPG, PNG ou GIF (max. 5MB)
                  </span>
                </div>

                {imagePreview && (
                  <div className="relative inline-block">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="h-40 w-auto object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors shadow-sm"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Publicando...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Publicar</span>
                </>
              )}
            </button>
        </form>
      </div>
    </div>
  );
}
